import * as React from 'react';
import { Input, FormPanel, SubmitButton, IconButton, FileDrop, Tooltip } from '@ixicare/ui';
import { getDefaultStateReducer, getDefaultReducerChange } from '~/utils/reducer';
import { useNavigate } from 'react-router-dom';
import { FloorPlanBody } from '@ixicare/resources';
import SiteStore from '../store/Site';
import { UserAction } from '~config/notification';
import classnames from 'classnames';
import styles from './SitePlaneMap.module.css';
import { t } from '~i18n';
import { MAX_FILE_SIZE, useMapFile } from '../hooks/useMapFile';

interface SiteFloorFormContainerProps {
  site: SiteStore;
}
const SiteFloorFormContainer: React.FC<SiteFloorFormContainerProps> = ({ site }) => {
  const navigate = useNavigate();
  const defaultFloor = site.planes.length;
  const iconRef = React.useRef<HTMLElement>(null!);
  const [state, dispatch] = React.useReducer(getDefaultStateReducer<FloorPlanBody>(), {
    floorName: `Floor ${defaultFloor}`,
    floorNumber: defaultFloor,
    lat: undefined,
    long: undefined,
  });
  const [mapFile, setMapFile] = React.useState<File>();
  const handleChange = getDefaultReducerChange<FloorPlanBody>(dispatch);

  const { onInvalid, isFileValid, setIsFileValid, handleFloorPlanChange } = useMapFile({
    setMapFile,
    notificationsStore: site.rootStore.notifications,
  });

  const handleSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      site
        .uploadFloorMap({ ...state, mapFile })
        .then((data) => {
          const navigateToSubitem = (attempts) => {
            try {
              navigate(`/sites/${site.uuid}/${data.id}`);
            } catch (error) {
              if (attempts > 0) {
                setTimeout(() => navigateToSubitem(attempts), 1000);
              } else {
                console.error('Navigation failed after retries:', error);
              }
            }
          };
          navigateToSubitem(2);
        })
        .catch((error) => {
          console.error('Error uploading floor map:', error);
          site.rootStore.notifications.addUserActionNotification({
            type: UserAction.SiteFloorCreate,
            notificationType: 'error',
            message: t('alerts.site.floor.upload.failure'),
          });
        });
    },
    [state, mapFile, site, navigate]
  );

  return (
    <FormPanel>
      <form onSubmit={handleSubmit}>
        <Input
          name="floorName"
          onChange={handleChange}
          value={state.floorName}
          label={t('site.floors.name')}
          required={true}
        />
        <Input
          name="floorNumber"
          onChange={handleChange}
          value={state.floorNumber}
          label={t('site.floors.floor')}
          required={true}
        />
        <Input name="lat" onChange={handleChange} value={state.lat} label={t('site.form.lat')} />
        <Input name="long" onChange={handleChange} value={state.long} label={t('site.form.long')} />

        <Tooltip title="File requirements" content={t('site.form.image.tooltip')} triggerRef={iconRef}>
          <>
            <label className={classnames(styles.FileDropLabel)}>
              {t('site.floors.file')} <IconButton ref={iconRef} icon="question" />
            </label>
            <FileDrop
              onChange={handleFloorPlanChange}
              onInvalid={onInvalid}
              isFileValid={isFileValid}
              setIsFileValid={setIsFileValid}
              maxSize={MAX_FILE_SIZE}
              value={mapFile}
              label={t('site.form.image.label.inner')}
              clearText={t('site.form.image.clearSelection')}
            />
          </>
        </Tooltip>
        <SubmitButton disabled={!isFileValid}>{t('site.floors.create')}</SubmitButton>
      </form>
    </FormPanel>
  );
};

export default SiteFloorFormContainer;
